// The task `status` enum, in one place. It was previously spelled out in six
// components, which is how adding a status turns into six near-identical edits
// and one file that quietly gets missed.
//
// Mirrors Task.status in the backend model — keep the two in step.

export const TASK_STATUSES = [
  { value: 'todo', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  // The firm is blocked ON THE CLIENT: no bank statements, no signed forms.
  // Deliberately separate from On Hold, which is the firm's own decision to
  // park the work — only one of the two is a list somebody has to chase.
  { value: 'waiting-client', label: 'Waiting on Client' },
  { value: 'completed', label: 'Completed' },
  { value: 'on-hold', label: 'On Hold' },
];

export const TASK_STATUS_LABEL = Object.fromEntries(
  TASK_STATUSES.map((s) => [s.value, s.label])
);

export const statusLabel = (value) => TASK_STATUS_LABEL[value] || value || '—';

// Statuses that mean "still live work" — mirrors OPEN_STATUSES server-side.
// The priority ladder, matching `Task.priority`'s enum exactly.
//
// ⚠️ IT LIVES HERE, BESIDE THE STATUSES, rather than being typed out in each
// dropdown. Two hand-written copies is how a fifth priority gets added to the
// model and silently fails to appear in half the app — the same reason
// TASK_STATUSES is not inlined anywhere. The labels are capitalised for a
// dropdown; StatusPill lower-cases its own.
export const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export const OPEN_TASK_STATUSES = TASK_STATUSES
  .filter((s) => s.value !== 'completed')
  .map((s) => s.value);

// --- Due-date framing -------------------------------------------------------

// The `?due=` windows the task list's DROPDOWN offers. Values match the
// server's filter (controllers/taskController#applyDueFilter).
export const DUE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due Today' },
  { value: 'week', label: 'Next 7 Days' },
  { value: 'month', label: 'Next 30 Days' },
  { value: 'undated', label: 'No Due Date' },
];

// The narrower windows behind the dashboard's five deadline tiles.
//
// ⚠️ REACHABLE BY URL BUT DELIBERATELY NOT IN THE DROPDOWN. Each one exists so
// a tile's number and the list it opens are the SAME number — "Overdue, more
// than 7 days: 3" pointed at the broad `overdue` filter would open every
// overdue task, and a count that changes the moment you click it is a count
// nobody trusts again. Putting all four in the dropdown as well would take it
// from six options to ten, most of them near-duplicates, to serve a case the
// dashboard already answers.
//
// The task list still has to LABEL whichever of these it arrives on, so they
// carry labels and the Select renders the active one as an extra option.
export const DUE_DEEP_LINKS = [
  { value: 'tomorrow', label: 'Due Tomorrow' },
  { value: 'next7', label: 'Due in 7 Days' },
  { value: 'overdue_recent', label: 'Overdue up to 7 Days' },
  { value: 'overdue_old', label: 'Overdue more than 7 Days' },
];

// Every `?due=` value the task list will accept from a URL.
export const ALL_DUE_VALUES = [...DUE_FILTERS, ...DUE_DEEP_LINKS].map((d) => d.value);

export const dueFilterLabel = (value) =>
  [...DUE_FILTERS, ...DUE_DEEP_LINKS].find((d) => d.value === value)?.label || value;

export const TASK_SORTS = [
  { value: 'dueDate', label: 'Due date (soonest)' },
  { value: 'dueDateDesc', label: 'Due date (latest)' },
  { value: 'created', label: 'Newest first' },
  { value: 'priority', label: 'Priority' },
  { value: 'taskNumber', label: 'Task number' },
];

const MS_PER_DAY = 86400000;

// Whole days between today (local midnight) and a date. Negative = in the past.
// Day-granular on purpose: a task due today is "due today" all day, not
// "overdue by 0.4 days" after lunch.
export function daysUntil(date) {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / MS_PER_DAY);
}

// How a task's due date should read in a list: the phrase, and the severity
// the UI colours it with. Completed tasks are never "overdue" — the date has
// stopped mattering once the work is done.
export function dueAging(task) {
  if (!task || !task.dueDate) {
    return { label: '—', tone: 'none', days: null };
  }
  const days = daysUntil(task.dueDate);
  if (task.status === 'completed') {
    return { label: 'Done', tone: 'done', days };
  }
  if (days < 0) {
    const n = Math.abs(days);
    return { label: `${n}d overdue`, tone: 'overdue', days };
  }
  if (days === 0) return { label: 'Due today', tone: 'today', days };
  if (days === 1) return { label: 'Tomorrow', tone: 'soon', days };
  if (days <= 7) return { label: `In ${days}d`, tone: 'soon', days };
  return { label: `In ${days}d`, tone: 'later', days };
}

// How long a task has been sitting on the client, for the chase list.
export function waitingAging(task) {
  if (!task || task.status !== 'waiting-client' || !task.waitingSince) return null;
  const days = Math.abs(daysUntil(task.waitingSince));
  return { days, label: days === 0 ? 'since today' : `${days}d` };
}
